import SentryService from '@services/SentryService';

import { AdaptiveMicLevelMeter, rmsFromTimeDomain } from './AdaptiveMicLevelMeter';

/** ~21 ms RMS window @48k — stable level (vs the 32-point FFT health detection uses). */
const FFT_SIZE = 1024;

/** Poll cadence — matches the waveform's render interval. */
const POLL_INTERVAL_MS = 50;

type LevelChangeHandler = (level: number) => void;

/**
 * MicLevelMeter — produces an honest 0-100 display level for the recording
 * waveform from a MediaStream.
 *
 * Deliberately decoupled from {@link AudioHealthMonitor}: it owns its own
 * AnalyserNode and reads TIME-DOMAIN samples (getFloatTimeDomainData) rather
 * than dB-scaled frequency bins, so the waveform is flat in a quiet room and
 * responsive to speech on any microphone. The level math lives in the pure,
 * unit-tested {@link AdaptiveMicLevelMeter}; this class only handles Web Audio
 * plumbing and lifecycle.
 */
export class MicLevelMeter {
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private pollTimerId: ReturnType<typeof setInterval> | null = null;
  private dataArray: Float32Array<ArrayBuffer> | null = null;
  private ownsAudioContext = false;

  private readonly meter = new AdaptiveMicLevelMeter();
  private onLevelChanged: LevelChangeHandler | null = null;
  private resumeFailureLogged = false;

  /** Set the callback for level updates (~50ms interval, normalized 0-100). */
  setOnLevelChanged(handler: LevelChangeHandler | null): void {
    this.onLevelChanged = handler;
  }

  /** Current adaptive noise-floor estimate in dBFS (for debugging/tuning). */
  getFloorDb(): number | null {
    return this.meter.getFloorDb();
  }

  /**
   * Start metering an audio stream.
   * Optionally accepts an external AudioContext to share with other nodes.
   */
  start(stream: MediaStream, externalAudioContext?: AudioContext): void {
    this.stop();
    this.meter.reset();
    this.resumeFailureLogged = false;

    if (externalAudioContext) {
      this.audioContext = externalAudioContext;
      this.ownsAudioContext = false;
    } else {
      this.audioContext = new AudioContext();
      this.ownsAudioContext = true;
    }

    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(err => this.logResumeFailure(err));
    }

    try {
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = FFT_SIZE;
      this.analyser.smoothingTimeConstant = 0; // we smooth the level ourselves
      this.dataArray = new Float32Array(this.analyser.fftSize);

      this.sourceNode = this.audioContext.createMediaStreamSource(stream);
      this.sourceNode.connect(this.analyser);
      // NB: do NOT connect the analyser to ctx.destination — that would play the
      // mic back through the speakers and cause feedback.
    } catch (error) {
      this.stop();
      throw error;
    }

    this.pollTimerId = setInterval(() => this.tick(), POLL_INTERVAL_MS);
  }

  /** Stop metering and clean up. Emits a resting level so the waveform settles flat. */
  stop(): void {
    if (this.pollTimerId !== null) {
      clearInterval(this.pollTimerId);
      this.pollTimerId = null;
    }

    if (this.sourceNode) {
      try {
        this.sourceNode.disconnect();
      } catch {
        // May already be disconnected
      }
      this.sourceNode = null;
    }

    if (this.analyser) {
      try {
        this.analyser.disconnect();
      } catch {
        // May already be disconnected
      }
      this.analyser = null;
    }

    if (this.ownsAudioContext && this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(() => {});
    }

    this.audioContext = null;
    this.dataArray = null;
    this.onLevelChanged?.(0);
  }

  private tick(): void {
    if (!this.analyser || !this.dataArray) return;

    // Resume if Chrome auto-suspended the idle AudioContext, mirroring the
    // health monitor — otherwise the meter would freeze at its last value.
    if (this.audioContext?.state === 'suspended') {
      this.audioContext.resume().catch(err => this.logResumeFailure(err));
      return;
    }

    this.analyser.getFloatTimeDomainData(this.dataArray);
    const rms = rmsFromTimeDomain(this.dataArray);
    const level = this.meter.update(rms); // 0..1
    this.onLevelChanged?.(Math.round(level * 100)); // 0-100, matches existing contract
  }

  /**
   * Breadcrumb a swallowed AudioContext.resume() failure — once per session, since
   * tick() can retry every 50ms and would otherwise flood the breadcrumb buffer.
   */
  private logResumeFailure(err: unknown): void {
    if (this.resumeFailureLogged) return;
    this.resumeFailureLogged = true;
    SentryService.addBreadcrumb({
      category: 'audio.meter',
      message: `MicLevelMeter: AudioContext.resume() failed — ${err instanceof Error ? err.message : String(err)}`,
      level: 'warning',
    });
  }
}
